import {
  AuthProvider,
  createIdentityClient,
  createSessionEventBus,
  SessionBusProvider,
} from '@skylabs-digital/react-identity-access';
import {
  GlobalStateProvider,
  SnackbarContainer,
  SnackbarProvider,
} from '@skylabs-digital/react-proto-kit';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AuthenticatedApiProvider } from './components/AuthenticatedApiProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { getBffBaseUrl } from './config/auth.config';
import { SelectedAppProvider } from './context/SelectedApp';
import { AppLayout } from './layouts/AppLayout';
import { ApiKeyNewPage } from './pages/ApiKeyNewPage';
import { ApiKeyRevealPage } from './pages/ApiKeyRevealPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { AppDetailPage } from './pages/AppDetailPage';
import { AppsPage } from './pages/AppsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DlqPage } from './pages/DlqPage';
import { LoginPage } from './pages/LoginPage';
import { MetricsPage } from './pages/MetricsPage';
import { NotificationDetailPage } from './pages/NotificationDetailPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { PreferencesPage } from './pages/PreferencesPage';
import { ProvidersPage } from './pages/ProvidersPage';
import { RateLimitsPage } from './pages/RateLimitsPage';
import { TemplateEditPage } from './pages/TemplateEditPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { TestInboxPage } from './pages/TestInboxPage';
import { UserInspectorPage } from './pages/UserInspectorPage';
import { WebhooksPage } from './pages/WebhooksPage';

/**
 * El bus de sesion, compartido por todo el arbol. Lo crea este modulo —y no
 * `AuthProvider`, que lo crearia solo— para que sea UNO: el mismo por el que
 * `AuthProvider` publica `session:invalid` y por el que cualquier otro
 * consumidor puede escucharlo.
 */
const sessionBus = createSessionEventBus();

/**
 * El cliente del BFF del backoffice (`/api/bo/auth/*`).
 *
 * Se construye UNA vez, fuera del arbol: `AuthProvider` re-crea su cliente
 * cuando la referencia cambia, y un `createIdentityClient(...)` inline en el
 * JSX seria una referencia nueva por render.
 *
 * `initialRoles: []` apaga el fetch del catalogo de roles de la app: esta
 * superficie autoriza por CONTEXTO (`(platform, admin)`, el preset
 * `platformAdmin` de RIA 5), no por roles de app — y una superficie de
 * plataforma no tiene catalogo.
 */
const identityClient = createIdentityClient({ baseUrl: getBffBaseUrl(), bus: sessionBus });

/**
 * Orden de providers, y por que este y no el canonico de la flota.
 *
 * El orden canonico (`ApiClientProvider > AppProvider > TenantProvider >
 * AuthProvider`) es para un frontend de PRODUCTO: uno que se declara contra
 * una app de idachu y opera dentro de un tenant. Este no es eso.
 *
 * El backoffice de noten es una SUPERFICIE DE PLATAFORMA: sus actores son
 * usuarios de sistema (`users.system_role`, `app_id IS NULL`), no tienen
 * membership, y su contexto es `(platform, admin)`. No hay `appId` que
 * declarar como pertenencia ni tenant que elegir — opera SOBRE ellos. Por eso
 * NO monta `AppProvider` ni `TenantProvider`, exactamente igual que el
 * backoffice de idachu, y a diferencia de resuelto o kommi.
 *
 * `AuthenticatedApiProvider` ya no depende de `AuthProvider` para nada: dejo
 * de inyectar el Bearer cuando la sesion paso a ser una cookie. Se conserva
 * adentro igual, porque el orden no molesta y mover cosas no era el objetivo
 * de este cambio.
 */
export function App() {
  return (
    <GlobalStateProvider>
      <SessionBusProvider bus={sessionBus}>
        <AuthProvider
          config={{
            client: identityClient,
            bus: sessionBus,
            // `eager` y no el default `lazy` de RIA 5: salvo `/login` y
            // `/inbox`, TODA pantalla esta detras de `PlatformAdminZone`, y el
            // propio `/login` declara la demanda con `useAccessDecision`. O sea
            // que el `GET /auth/bootstrap` va a salir en el primer frame igual;
            // pedirlo de entrada ahorra una pasada de render.
            bootstrap: 'eager',
            initialRoles: [],
          }}
        >
          <AuthenticatedApiProvider>
            {/* Adentro del provider de API porque el selector lista apps, y
              arriba del Router para que la seleccion sobreviva a la navegacion. */}
            <SelectedAppProvider>
              <Router>
                <SnackbarProvider defaultDuration={4000} maxVisible={3}>
                  <SnackbarContainer position="top-right" />
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route element={<ProtectedRoute />}>
                      {/* `/inbox` estaba fuera de ProtectedRoute y su API
                      respondia anonima: en QA eso publicaba los magic links de
                      idachu a cualquiera con la URL (noten-0s2). Va adentro,
                      igual que el resto. */}
                      <Route path="/inbox" element={<TestInboxPage />} />
                      <Route element={<AppLayout />}>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/notifications/:id" element={<NotificationDetailPage />} />
                        <Route path="/templates" element={<TemplatesPage />} />
                        <Route path="/templates/new" element={<TemplateEditPage />} />
                        <Route path="/templates/:id/edit" element={<TemplateEditPage />} />
                        <Route path="/apps" element={<AppsPage />} />
                        <Route path="/apps/:id" element={<AppDetailPage />} />
                        <Route path="/preferences" element={<PreferencesPage />} />
                        <Route path="/dlq" element={<DlqPage />} />
                        <Route path="/webhooks" element={<WebhooksPage />} />
                        <Route path="/users" element={<UserInspectorPage />} />
                        <Route path="/api-keys" element={<ApiKeysPage />} />
                        <Route path="/api-keys/new" element={<ApiKeyNewPage />} />
                        <Route path="/api-keys/:id/reveal" element={<ApiKeyRevealPage />} />
                        {/* Antes iban detras de un `requiredRoles` que miraba
                        `user.role` y por eso no dejaba pasar a nadie (ver
                        `ProtectedRoute`). En una superficie de plataforma no
                        hay un segundo nivel que distinguir: todo el que entro
                        es actor de plataforma. */}
                        <Route path="/metrics" element={<MetricsPage />} />
                        <Route path="/providers" element={<ProvidersPage />} />
                        <Route path="/rate-limits" element={<RateLimitsPage />} />
                      </Route>
                    </Route>
                  </Routes>
                </SnackbarProvider>
              </Router>
            </SelectedAppProvider>
          </AuthenticatedApiProvider>
        </AuthProvider>
      </SessionBusProvider>
    </GlobalStateProvider>
  );
}
